import {StoresInstance} from '@stores'
import {action, runInAction} from 'mobx'
import {Fragment, useEffect, useMemo} from 'react'
import {React, observable, classNames, observer, useAsObservableSource, computed} from '@api'
import {useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {Actions, BorderNode, CLASSES, ErrorBoundary, I18nLabel, ILayoutCallbacks, TabNode, TabSetNode} from '@flexlayout'
import {useHotkeys} from 'react-hotkeys-hook'
import * as css from './Tab.css'

/** @hidden @internal */
export interface ITabProps {
  layout: ILayoutCallbacks;
  selected: boolean;
  node: TabNode;
  factory: (node: TabNode) => React.ReactNode;
}

/// region Store

export class TabStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: ITabProps) {
    super(stores)
  }

  @computed get tab() { return this.props.node}
  /**
   * Auto load after some time onscreen even if not the active tab
   */
  @observable prefetch = true

  registerEffects = () => {
    const {props: {node}, prefetch} = this
    let prefetchTimeoutId : number

    if (prefetch && !node.everVisible) {
      prefetchTimeoutId = window.setTimeout(() => {
        runInAction(() => node.everVisible = true)
      }, (2000 * node.indexInParent) + _.random(1000, 10000))
    }

    return () => {
      prefetchTimeoutId && clearTimeout(prefetchTimeoutId)
      //console.log(`Unmount tab ${this.tab.id}`)
    }
  }

  @computed get style(): Record<string, any> {
    return this.props.node._styleWithPosition({})
  }

  @action onMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if ((e as React.MouseEvent).button === 2) return

    const {props: {node, layout}} = this
    const parent = node.parent as TabSetNode
    if (parent.type === TabSetNode.TYPE) {
      if (!parent.isActive) {
        layout.doAction(Actions.setActiveTabset(parent.id))
      }
    }
  }

  @computed get childComponent() {
    const {props: {factory}, tab} = this
    if (!tab.everVisible) return null

    return factory(tab)
  }
}

export const TabStoreContext = React.createContext<TabStore>(null)
export const useTabStore = () => React.useContext(TabStoreContext)

/// endregion

/** @hidden @internal */
export const Tab = observer<ITabProps>(function Tab(props) {
  const {layout, selected, node, factory, node: {parent}} = props,
    {flexLayout} = useStores(),
    obsProps = useAsObservableSource(props),
    store = useVipLocalStore(s => new TabStore(s, obsProps)),
    {onMouseDown, childComponent} = store

  useEffect(() => store.registerEffects(), [])

  // Don't render tabs that we will render ourselves via a copy
  if (flexLayout.borderForKnownElement[node.id]) return null

  const cm = layout.getClassName

  const parentNode = node.parent as TabSetNode | BorderNode

  let className = cm(CLASSES.FLEXLAYOUT__TAB)
  if (parentNode instanceof BorderNode) {
    className += ' ' + cm(CLASSES.FLEXLAYOUT__TAB_BORDER)
    className += ' ' + cm(CLASSES.FLEXLAYOUT__TAB_BORDER_ + parentNode.location.name)
  }

  useHotkeys('leftArrow', () => {
    node.tabSet?.selectPreviousTab()
  })

  useHotkeys('rightArrow', () => {
    node.tabSet?.selectNextTab()
  })

  // console.debug(`Rendering tab ${node.id} - ${node.name}`, {style: store.style})

  return (
    <TabStoreContext.Provider value={store}>
      <div className={classNames(className, css.root, node.className /*, 'animate__animated ', node.isMaximized ? flexLayout.animations.maximize : flexLayout.animations.restore*/)}
           onMouseDown={onMouseDown}
           onTouchStart={onMouseDown}
           tabIndex={node.indexInParent}
           data-id={node.id}
           data-visible={node.isVisible}
           data-selected={node.isSelected}
           data-maximized={node.isMaximized}
           data-component={node.component}
           data-offscreen-direction={node.offscreenDirection}
           onContextMenu={flexLayout.onContextMenu_forTabNode(node)}
           style={flexLayout.isMaximized ? undefined : store.style}>
        <ErrorBoundary message={props.layout.i18nName(I18nLabel.Error_rendering_component)}>
          <Fragment>{childComponent}</Fragment>
        </ErrorBoundary>
      </div>
    </TabStoreContext.Provider>
  )
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/Tab.tsx